import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CALENDAR_ICON from "../img/calendar_icon.svg";
import IconButton from "../shared/IconButton";
import { Input } from "./ui/input";
import {
  formatIsoDateForDisplay,
  normalizeBirthDateValue,
  parseIsoDate,
  toIsoDate,
} from "../Utilities/dateUtils";

const MarketingBirthdayDatePicker = ({ value, onChange }) => {
  const calendarWrapperRef = useRef(null);

  const dateValue = normalizeBirthDateValue(value);
  const displayDateValue = formatIsoDateForDisplay(dateValue);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = toIsoDate(today);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const selectedDate = parseIsoDate(dateValue);
    const baseDate = selectedDate || today;
    return new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  });

  const weekdayLabelFormatter = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
  });
  const monthLabelFormatter = new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  });

  const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
    weekdayLabelFormatter.format(new Date(2024, 0, 7 + index))
  );

  const selectedDate = parseIsoDate(dateValue);

  const firstDayOfMonth = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1
  );
  const firstWeekdayIndex = firstDayOfMonth.getDay();
  const daysInMonth = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() + 1,
    0
  ).getDate();

  const dayCells = [];

  for (let i = 0; i < firstWeekdayIndex; i += 1) {
    dayCells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    dayCells.push(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day));
  }

  while (dayCells.length % 7 !== 0) {
    dayCells.push(null);
  }

  const monthLabel = monthLabelFormatter.format(visibleMonth);

  const currentMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);
  const nextMonthDate = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() + 1,
    1
  );
  const hasNextMonth = nextMonthDate <= currentMonthDate;

  useEffect(() => {
    if (!dateValue) return;
    const parsedDate = parseIsoDate(dateValue);
    if (!parsedDate) return;
    setVisibleMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
  }, [dateValue]);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!calendarWrapperRef.current?.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  const handleDatePickerOpen = () => {
    setIsCalendarOpen(true);
  };

  const handleDaySelect = (dayDate) => {
    const normalizedDayDate = new Date(dayDate);
    normalizedDayDate.setHours(0, 0, 0, 0);

    if (normalizedDayDate > today) return;

    onChange?.(toIsoDate(normalizedDayDate));
    setIsCalendarOpen(false);
  };

  const isSelectedDate = (dayDate) =>
    selectedDate && toIsoDate(dayDate) === toIsoDate(selectedDate);

  return (
    <div className="marketing-date-wrapper" ref={calendarWrapperRef}>
      <Input
        type="text"
        value={displayDateValue}
        placeholder="00 - 00 - 0000"
        dir="ltr"
        className="marketing-date-input"
        data-testid="marketing-birthday-input"
        readOnly
        onClick={handleDatePickerOpen}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleDatePickerOpen();
          }
        }}
      />
      <div className="marketing-date-adornment">
        <IconButton
          className="marketing-icon-btn p-2"
          data-testid="marketing-birthday-calendar-toggle"
          onClick={() => setIsCalendarOpen((prev) => !prev)}
        >
          <img src={CALENDAR_ICON}></img>
        </IconButton>
      </div>
      {isCalendarOpen && (
        <div
          className="marketing-date-popup"
          role="dialog"
          aria-modal="false"
          data-testid="marketing-birthday-calendar"
        >
          <div className="marketing-date-popup-header">
            <IconButton
              className="marketing-date-nav-btn"
              onClick={() =>
                setVisibleMonth(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                )
              }
              data-testid="marketing-birthday-calendar-prev-month"
            >
              <ChevronLeft size={16} />
            </IconButton>
            <p className="marketing-date-popup-title">{monthLabel}</p>
            <IconButton
              className={`marketing-date-nav-btn ${!hasNextMonth ? "is-disabled" : ""}`}
              onClick={() => {
                if (!hasNextMonth) return;
                setVisibleMonth(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                );
              }}
              data-testid="marketing-birthday-calendar-next-month"
            >
              <ChevronRight size={16} />
            </IconButton>
          </div>

          <div className="marketing-date-weekdays" aria-hidden="true">
            {weekdayLabels.map((label) => (
              <span key={label} className="marketing-date-weekday">
                {label}
              </span>
            ))}
          </div>

          <div className="marketing-date-days">
            {dayCells.map((dayDate, index) => {
              if (!dayDate) {
                return (
                  <span
                    key={`empty-${index}`}
                    className="marketing-date-day-placeholder"
                  />
                );
              }

              const isDisabled = toIsoDate(dayDate) > maxDate;
              const isSelected = isSelectedDate(dayDate);

              return (
                <button
                  key={toIsoDate(dayDate)}
                  type="button"
                  className={`marketing-date-day ${isSelected ? "is-selected" : ""}`}
                  onClick={() => handleDaySelect(dayDate)}
                  disabled={isDisabled}
                  data-testid={`marketing-birthday-day-${toIsoDate(dayDate)}`}
                >
                  {dayDate.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingBirthdayDatePicker;
